import React, { useMemo } from 'react';
import {
  Paper, Box, ToggleButtonGroup, ToggleButton, Chip, Autocomplete, TextField,
  createFilterOptions,
  Typography, Stack, Tooltip, Divider, IconButton, Switch, FormControlLabel,
} from '@mui/material';
import CableIcon from '@mui/icons-material/Cable';
import LanIcon from '@mui/icons-material/Lan';
import RouterIcon from '@mui/icons-material/Router';
import HealingIcon from '@mui/icons-material/Healing';
import LayersIcon from '@mui/icons-material/Layers';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { colorForVid } from './vlanColors';

/**
 * ModeToolbar — substitui sidebar de filtros.
 * 2 linhas no topo da página:
 *   Row 1 (em TopologyPage.js): empresa, search, stats, layout, rebuild
 *   Row 2 (este componente):
 *     - OSI mode toggle [Tudo | L1 | L2 | L3 | Health]
 *     - Controles dependentes do mode (inline, compactos)
 *
 * Zero sidebar. Mapa ocupa 100% da largura.
 */

// Paleta unificada: essas cores são a ÚNICA source-of-truth. Tanto os chips
// do toolbar quanto as edges no Cytoscape usam os mesmos hexes — se mudar
// aqui, espelhar em CyTopologyMap.js (edge.kind-bgp/ospf_adj/etc).
const OSI_MODES = [
  { key: 'all',    label: 'Tudo',   icon: <LayersIcon fontSize="small" /> },
  { key: 'l1',     label: 'L1',     icon: <CableIcon fontSize="small" />,  color: '#06b6d4' },  // cyan-500
  { key: 'l2',     label: 'L2',     icon: <LanIcon fontSize="small" />,    color: '#14b8a6' },  // teal-500
  { key: 'l3',     label: 'L3',     icon: <RouterIcon fontSize="small" />, color: '#f59e0b' },  // amber-500
  { key: 'health', label: 'Health', icon: <HealingIcon fontSize="small" />, color: '#ef4444' }, // red-500
];

// L3 protocols — só protocolos de roteamento de verdade. `p2p /30` foi removido
// daqui porque é evidência FÍSICA (L1) de um cabo entre 2 hosts, não protocolo.
// Cores DISTINTAS + saturadas pra não confundir com mode colors acima.
// Paleta quente/frio pra contraste máximo entre protocolos:
//   BGP   (external, heat) → orange (quente)
//   OSPF  (internal IGP)   → sky (frio)
//   MPLS  (transport)      → pink (hot, destaca de OSPF azul)
//   Static (manual)        → slate (neutro)
const L3_PROTOCOLS = [
  { key: 'bgp',          label: 'BGP',    color: '#f97316', hostFlag: 'has_bgp' },   // orange-500
  { key: 'ospf_adj',     label: 'OSPF',   color: '#0ea5e9', hostFlag: 'has_ospf' },  // sky-500
  { key: 'mpls',         label: 'MPLS',   color: '#d946ef', hostFlag: 'has_mpls' },  // fuchsia-500 (mais magenta, evita leitura como vermelho)
  { key: 'static_route', label: 'Static', color: '#64748b', hostFlag: null },        // slate-500
];

export default function ModeToolbar({
  // Quantas arestas cada chip de protocolo afeta no grafo ATUAL (Map key->n).
  // Chip com 0 não é renderizado, e os demais mostram a contagem antes do
  // clique. Sem isso o operador clicava num chip e nada acontecia — e um
  // controle sem efeito ensina que a interface mente, o que contamina a
  // confiança nos filtros que funcionam.
  protocolCounts,
  // OSI modes (multi-select: Set<string>)
  osiModes, onChangeOsiModes,
  l3Protocols, onChangeL3Protocols,
  l3StrictHosts, onChangeL3StrictHosts,

  // L1 — port picker
  interfaces,  // flat list [{host, host_label, iface_name, status, lag_parent, vlans}]
  selectedIface, onChangeSelectedIface,

  // L2 — VLAN/Ring
  vlansIndex,
  selectedVlans, onChangeSelectedVlans,
  rings,  // [{ring_id, members, role, ...}]
  highlightedRing, onChangeHighlightedRing,
}) {
  const handleMode = (_, next) => {
    // next é um array (multi-select). Se usuário desliga tudo, entende como "all".
    const modeSet = new Set(next || []);
    if (modeSet.size === 0) modeSet.add('all');
    // "all" é mutuamente exclusivo com os outros
    if (modeSet.has('all') && modeSet.size > 1) {
      // Se clicou 'all' por último, deixa só 'all'; senão remove 'all'
      if (!osiModes?.has('all')) {
        onChangeOsiModes(new Set(['all']));
        return;
      }
      modeSet.delete('all');
    }
    onChangeOsiModes(modeSet);
  };
  // helper pra checar mode ativo
  const isActive = (key) => osiModes?.has(key);

  const toggleL3Proto = (key) => {
    const next = new Set(l3Protocols);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChangeL3Protocols(next);
  };


  // L1 — port options
/**
 * Filtro dos seletores grandes.
 *
 * O Autocomplete do MUI monta no DOM TODA opção que passa no filtro, e o de
 * porta tem 6.028 na TCHE — com o campo vazio ele tentava desenhar as 6.028 de
 * uma vez. Não é o dado que pesa (já veio no payload do mapa, 391 KB); é o
 * DOM.
 *
 * Duas travas: nada aparece antes de digitar 2 caracteres, e o resultado é
 * cortado em LIMITE_OPCOES. Buscar 3 letras num seletor de porta é o que a
 * pessoa ia fazer de qualquer jeito — rolar 6 mil itens nunca foi o caminho.
 */
const LIMITE_OPCOES = 40;
const MIN_BUSCA = 2;

function filtroParcial(stringify) {
  const base = createFilterOptions({ limit: LIMITE_OPCOES, stringify });
  return (opcoes, estado) => {
    const q = (estado.inputValue || '').trim();
    if (q.length < MIN_BUSCA) return [];
    return base(opcoes, estado);
  };
}

const filtroIface = filtroParcial((o) => `${o.label} ${o.name} ${o.host_label || ''}`);
const filtroVlan = filtroParcial((o) => String(o.vid));

  const ifaceOptions = useMemo(() => {
    if (!interfaces) return [];
    return interfaces.map((i) => ({
      key: `${i.host_id}:${i.name}`,
      host_id: i.host_id,
      host_label: i.host_label,
      name: i.name,
      label: `${i.host_label || ''} / ${i.name}`,
      status: i.admin_down ? 'down' : 'up',
      lag_parent: i.lag_parent,
      vlans: i.vlans || [],
    }));
  }, [interfaces]);

  // L2 — VLAN options
  const vlanOptions = useMemo(() => (
    (vlansIndex || []).map((v) => ({
      vid: v.vid,
      label: `VLAN ${v.vid}  ·  ${v.hosts?.length || 0}h ${v.edges?.length || 0}e`,
    }))
  ), [vlansIndex]);

  // L2 — Ring options
  const ringOptions = useMemo(() => (
    (rings || []).map((r) => ({
      ring_id: r.ring_id,
      label: `Ring ${r.ring_id}` + (r.control_vlan ? ` · RAPS ${r.control_vlan}` : ''),
      rpl_owner: r.rpl_owner,
    }))
  ), [rings]);

  return (
    <Paper sx={{
      p: 1,
      mb: 1,
      bgcolor: '#f8fafc',
      color: '#0f172a',
      '& .MuiTypography-root': { color: '#0f172a' },
      '& .MuiTypography-caption': { color: '#475569' },
      '& .MuiInputBase-root': { bgcolor: '#ffffff' },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
      '& .MuiInputLabel-root': { color: '#475569' },
      '& .MuiSvgIcon-root': { color: '#475569' },
      '& .MuiDivider-root': { borderColor: '#cbd5e1' },
    }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {/* OSI mode selector — multi-select. Operador pode combinar L1+L2
            pra ver porta física + VLAN juntos, por exemplo. "Tudo" é exclusivo. */}
        <ToggleButtonGroup
          value={Array.from(osiModes || new Set(['all']))}
          size="small"
          onChange={handleMode}
          sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.3, fontSize: 11, gap: 0.3 } }}
        >
          {OSI_MODES.map((m) => (
            <Tooltip key={m.key} title={`${m.label} mode`}>
              <ToggleButton
                value={m.key}
                sx={{
                  fontWeight: 700,
                  color: isActive(m.key) ? (m.color || 'inherit') : 'text.secondary',
                  '&.Mui-selected': {
                    color: m.color || 'primary.main',
                    bgcolor: m.color ? `${m.color}22` : 'primary.light',
                  },
                }}
              >
                {m.icon}
                {m.label}
              </ToggleButton>
            </Tooltip>
          ))}
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* L1 — port picker + show down only */}
        {isActive('l1') && (
          <>
            <Typography variant="caption" color="text.secondary">
              <CableIcon fontSize="inherit" /> Porta:
            </Typography>
            <Autocomplete
              size="small"
              options={ifaceOptions}
              filterOptions={filtroIface}
              value={selectedIface}
              onChange={(_, v) => onChangeSelectedIface(v)}
              getOptionLabel={(o) => o?.label || ''}
              isOptionEqualToValue={(a, b) => a?.key === b?.key}
              sx={{ minWidth: 320 }}
              noOptionsText={`digite ${MIN_BUSCA}+ letras — ${ifaceOptions.length} portas`}
              renderInput={(params) => (
                <TextField {...params} size="small"
                  placeholder={`buscar entre ${ifaceOptions.length} portas...`} />
              )}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.key} sx={{ flexDirection: 'column', alignItems: 'flex-start !important', py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {o.name}
                    </Typography>
                    {o.lag_parent && (
                      <Chip label={`⧉ ${o.lag_parent}`} size="small" sx={{ height: 16, fontSize: 9 }} />
                    )}
                    {o.status === 'down' && (
                      <Chip label="down" size="small" color="error" sx={{ height: 16, fontSize: 9 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {o.host_label} · {o.vlans?.length || 0} VLANs
                  </Typography>
                </Box>
              )}
            />
            {selectedIface && (
              <Chip
                label={`${selectedIface.vlans.length} VLANs`}
                size="small"
                onDelete={() => onChangeSelectedIface(null)}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {ifaceOptions.length} portas físicas
            </Typography>
          </>
        )}

        {/* L2 — VLAN multi-select + Ring picker */}
        {isActive('l2') && (
          <>
            <Typography variant="caption" color="text.secondary">
              <LanIcon fontSize="inherit" /> VLANs:
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={vlanOptions}
              filterOptions={filtroVlan}
              noOptionsText={`digite ${MIN_BUSCA}+ dígitos — ${vlanOptions.length} VLANs`}
              value={vlanOptions.filter((v) => selectedVlans?.has(v.vid))}
              onChange={(_, value) => {
                const next = new Set(value.map((v) => v.vid));
                onChangeSelectedVlans(next);
              }}
              getOptionLabel={(o) => `VLAN ${o.vid}`}
              isOptionEqualToValue={(a, b) => a?.vid === b?.vid}
              disableCloseOnSelect
              limitTags={4}
              filterSelectedOptions
              sx={{ minWidth: 320 }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tp } = getTagProps({ index });
                  return (
                    <Chip
                      key={option.vid}
                      label={option.vid}
                      size="small"
                      sx={{
                        bgcolor: colorForVid(option.vid),
                        color: '#0f172a',
                        fontWeight: 700,
                        height: 20,
                      }}
                      {...tp}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField {...params} size="small"
                  placeholder={`buscar entre ${vlanOptions.length} VLANs...`} />
              )}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              <DonutLargeIcon fontSize="inherit" /> Ring:
            </Typography>
            <Autocomplete
              size="small"
              options={ringOptions}
              value={ringOptions.find((r) => r.ring_id === highlightedRing) || null}
              onChange={(_, v) => onChangeHighlightedRing(v?.ring_id ?? null)}
              getOptionLabel={(o) => o?.label || ''}
              isOptionEqualToValue={(a, b) => a?.ring_id === b?.ring_id}
              sx={{ minWidth: 220 }}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="ERPS ring..." />
              )}
            />
          </>
        )}

        {/* L3 — Protocol chips + 1 Switch único "Só hosts com protocolo".
            Chip click = protocolo entra/sai da seleção (filtra edges por kind).
            Switch = quando ON, esconde hosts que NÃO têm nenhum dos protocolos selecionados. */}
        {isActive('l3') && (
          <>
            <Typography variant="caption" color="text.secondary">
              <RouterIcon fontSize="inherit" /> Protocolo:
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {L3_PROTOCOLS.filter((p) => {
                // sem contagem disponível, mostra tudo (compat)
                if (!protocolCounts) return true;
                return (protocolCounts.get(p.key) || 0) > 0;
              }).map((p) => {
                const active = l3Protocols?.has(p.key);
                const n = protocolCounts ? protocolCounts.get(p.key) : null;
                return (
                  <Chip
                    key={p.key}
                    label={n == null ? p.label : `${p.label} (${n})`}
                    size="small"
                    onClick={() => toggleL3Proto(p.key)}
                    sx={{
                      bgcolor: active ? p.color : 'transparent',
                      color: active ? '#fff' : 'text.primary',
                      border: `1.5px solid ${p.color}`,
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: active ? p.color : `${p.color}22` },
                    }}
                  />
                );
              })}
            </Stack>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Esconde hosts que NÃO têm os protocolos selecionados">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!l3StrictHosts}
                    onChange={(e) => onChangeL3StrictHosts(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">só hosts com protocolo</Typography>}
                sx={{ m: 0 }}
              />
            </Tooltip>
          </>
        )}

        {/* Health mode — hint */}
        {isActive('health') && (
          <Typography variant="caption" color="text.secondary">
            <HealingIcon fontSize="inherit" /> Modo war-room: só hosts com issues/alarmes ativos
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
